import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { applyLanguage } from "@/i18n";

const OPTIONS = [
    { code: "en", label: "English" },
    { code: "fi", label: "Suomi" },
];

// Settings → Language / Kieli. Saved on the signed-in account (shared
// restaurant logins share it); cached on the device for first paint.
export function LanguageSection() {
    const { t, i18n } = useTranslation();
    const { setLanguagePreference } = useApp();
    const [saving, setSaving] = useState(false);
    const current = i18n.language === "fi" ? "fi" : "en";

    const choose = async (code) => {
        if (code === current || saving) return;
        setSaving(true);
        try {
            await setLanguagePreference(code);
            // i18n.t (not the render-time t) so the toast uses the NEW language.
            toast.success(i18n.t("language.saved", "Language saved"));
        } catch (err) {
            applyLanguage(current);
            toast.error(apiErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section data-testid="settings-language-section" className="space-y-4">
            <div>
                <h3 className="font-display text-2xl font-bold text-neutral-900">Language / Kieli</h3>
                <p className="text-sm text-neutral-500 mt-1 max-w-xl">
                    {t("language.description",
                        "Choose the interface language for this account. Names, recipes and other entered content are not translated.")}
                </p>
            </div>
            <div role="radiogroup" aria-label="Language / Kieli" className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
                {OPTIONS.map((o) => {
                    const active = o.code === current;
                    return (
                        <button
                            key={o.code}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            lang={o.code}
                            disabled={saving}
                            data-testid={`settings-language-${o.code}`}
                            onClick={() => choose(o.code)}
                            className={`min-h-[48px] px-4 rounded-lg border text-left inline-flex items-center justify-between gap-3 text-sm font-semibold transition-colors disabled:opacity-60 ${
                                active ? "border-neutral-900 bg-neutral-900 text-white"
                                    : "border-neutral-200 bg-white text-neutral-900 hover:bg-neutral-50"}`}
                        >
                            {o.label}
                            {active && <Check className="h-4 w-4" aria-hidden="true" />}
                        </button>
                    );
                })}
            </div>
        </section>
    );
}
